import { Button, LinearProgress, DialogActions, Step, StepButton, Stepper, capitalize } from '@tunasong/ui-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import { shortUuid } from '@tunasong/models'
import { useGlobalAppContext, type Template, type TunaPlugin } from '@tunasong/plugin-lib'
import type { Entity, EntityType, Persisted } from '@tunasong/schemas'
import { entityTypeNames, isPersisted } from '@tunasong/schemas'
import { Dialog, Typography, useDimensions, useNavigateToEntity } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useMemo, useState } from 'react'
import invariant from 'tiny-invariant'
import ConfigureEntity from './configure-entity.js'
import CreateEntityGrid from './create-entity-grid.js'
import styles from './create-entity-dialog.module.css'
import { canConfigureEntity } from './util.js'

export interface CreateEntityDialogProps {
  plugins: TunaPlugin[]
  parent: Persisted<Entity>
  open: boolean
  onClose: (props?: { restoreLayout?: boolean }) => void
}

const steps = ['Select type', 'Configure']

export const CreateEntityDialog: FC<CreateEntityDialogProps> = props => {
  const { plugins, parent = null, open, onClose } = props
  const navigateToEntity = useNavigateToEntity()

  const [newEntityTemplate, setNewEntityTemplate] = useState<Entity | Persisted<Entity>>()

  const [activeStep, setActiveStep] = useState(0)
  const [completed] = useState<Record<number, boolean>>({})
  const handleStep = (idx: number) => () => setActiveStep(idx)

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1)
  }
  const handlePrev = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const [activeTemplate, setActiveTemplate] = useState<Template>()

  const [loading, setLoading] = useState(false)
  const { createEntity } = graphHooks.useEntityCreate()
  const { cloneEntity } = graphHooks.useEntityClone()

  const globalContext = useGlobalAppContext()
  const handleCreate = async (newEntity: Entity) => {
    invariant(newEntity, 'newEntity is undefined')
    invariant(newEntity.type, 'newEntity.type is undefined')

    setLoading(true)

    const plugin = plugins?.find(p => p.node?.type === newEntity.type)
    invariant(plugin, `Plugin for type ${newEntity.type} not found`)

    const onCreate = activeTemplate?.onCreate

    // If newEntity has an id, it's a persisted, existing entity, and we need to clone the existing entity.
    let e: Persisted<Entity> | undefined
    const persisted = isPersisted(newEntity)
    if (persisted) {
      e = await cloneEntity({
        entity: newEntity,
        updatedProps: { ...newEntity, parentId: parent?.id },
      })
    } else {
      e = await createEntity({ parent, entity: { id: shortUuid(), ...newEntity } })
    }

    if (onCreate) {
      await onCreate({
        entity: e,
        parent,
        globalContext,
      })
    }

    setLoading(false)
    setActiveStep(0)
    setNewEntityTemplate(undefined)

    /** If the type has an Edit view, navigate to that in the edit layout */
    const layouts = Object.keys(plugin.layouts ?? {})
    const layout = layouts.find(v => v === 'Edit') ? 'Edit' : undefined
    navigateToEntity(e, { layout })

    // We want to close the dialog, but not restore the view since we've created a new entity
    onClose({ restoreLayout: false })
  }

  const allowedEntities: readonly EntityType[] = useMemo(() => {
    const parentPlugin = plugins.find(p => p.node?.type === parent?.type)
    if (!parentPlugin?.allowChildrenTypes || parentPlugin?.allowChildrenTypes === true) {
      return entityTypeNames
    }
    return []
  }, [parent?.type, plugins])

  const handleConfigured = ({ entity, complete }: { entity: Entity; complete?: boolean }) => {
    setNewEntityTemplate({ ...entity, parentId: parent?.id })
    if (complete) {
      void handleCreate(entity)
    }
  }
  const handleError = () => setNewEntityTemplate(undefined)

  const handleComplete = () => {
    invariant(newEntityTemplate, 'newEntity must be defined')
    void handleCreate(newEntityTemplate)
  }

  const activePlugin = plugins.find(p => p.node?.type === activeTemplate?.template.type)

  const hasNext = activeStep < steps.length - 1 && canConfigureEntity(activePlugin, activeTemplate)
  const hasPrev = activeStep > 0

  const handleSelect = (template: Template | null) => {
    setActiveTemplate(template ?? undefined)
    /**
     * Resolve the plugin from the template being selected, not from
     * `activePlugin` — that derives from `activeTemplate`, which the setState
     * above has not applied yet, so it still describes the *previous*
     * selection (and is undefined on the first one).
     */
    const selectedPlugin = plugins.find(p => p.node?.type === template?.template.type)
    // If there is no configuration step, we can skip to the end
    if (!canConfigureEntity(selectedPlugin, template ?? undefined)) {
      setNewEntityTemplate(template?.template)
    }
  }

  const { isSmallOrSmaller } = useDimensions()
  const fullScreen = isSmallOrSmaller
  const handleClose = () => {
    onClose()
  }

  return (
    <Dialog
      title={`New ${capitalize(newEntityTemplate?.type ?? 'Entity')}`}
      open={open}
      className={styles.dialog}
      maxWidth={fullScreen ? undefined : 'md'}
      fullWidth={true}
      fullScreen={fullScreen}
      showClose={false}
      onClose={handleClose}
    >
      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step key={label} completed={completed[index]}>
            <StepButton color="inherit" onClick={handleStep(index)}>
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      {loading || !parent ? (
        <LinearProgress className={styles.progress} />
      ) : (
        <>
          <div className={styles.content}>
            <Typography variant="caption">
              You can create the following page types. Note that some types may not be available as children of a{' '}
              {capitalize(parent.type)}.
            </Typography>
            {activeStep === 0 ? (
              <CreateEntityGrid
                plugins={plugins}
                onSelect={handleSelect}
                selected={activeTemplate}
                // showCategories={allowedEntities.length > 5}
                showCategories={true}
                allowedChildEntities={allowedEntities}
              />
            ) : null}
            {activeStep === 1 && activePlugin ? (
              <ConfigureEntity
                plugin={activePlugin}
                template={activeTemplate}
                onConfigured={handleConfigured}
                onError={handleError}
              />
            ) : null}
          </div>

          <DialogActions>
            <Button disabled={!hasPrev} onClick={handlePrev} className={styles.actionButton}>
              Prev
            </Button>

            {hasNext ? (
              <Button disabled={!hasNext || !activeTemplate} onClick={handleNext} className={styles.actionButton}>
                Next
              </Button>
            ) : null}
            {!hasNext ? (
              <Button disabled={!newEntityTemplate} className={styles.actionButton} onClick={handleComplete}>
                Complete
              </Button>
            ) : null}
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default CreateEntityDialog
